import { motion } from 'framer-motion';

// Character animation variants
const floatAnimation = {
  y: [0, -8, 0],
  transition: { duration: 3, repeat: Infinity, ease: "easeInOut" }
};

const eyeBlinkAnimation = {
  scale: [1, 1.1, 1],
  transition: { duration: 2, repeat: Infinity }
};

// Base character wrapper with floating effect
interface CharacterWrapperProps {
  children: React.ReactNode;
  className?: string;
  floatDelay?: number;
}

export function CharacterWrapper({ children, className = '', floatDelay = 0 }: CharacterWrapperProps) {
  return (
    <motion.div
      animate={{ y: [0, -8, 0] }}
      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: floatDelay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// Aria - Social Media Manager (Pink astronaut with bow)
export function AriaCharacter({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizes = {
    sm: { helmet: 'w-16 h-16', body: 'w-12 h-10', eyes: 'w-2 h-3', gap: 'gap-2' },
    md: { helmet: 'w-24 h-24', body: 'w-20 h-16', eyes: 'w-3 h-4', gap: 'gap-3' },
    lg: { helmet: 'w-40 h-40', body: 'w-32 h-28', eyes: 'w-7 h-9', gap: 'gap-6' },
  };
  const s = sizes[size];

  return (
    <div className="relative">
      {/* Glow effect */}
      <div className="absolute -inset-4 bg-gradient-to-br from-pink-400/30 to-purple-500/30 rounded-full blur-2xl" />

      {/* Helmet with bow */}
      <div className={`relative ${s.helmet}`}>
        {/* Bow on top */}
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-10">
          <div className="relative">
            <div className="w-4 h-3 bg-gradient-to-r from-pink-400 to-pink-500 rounded-full transform -rotate-45 absolute -left-3" />
            <div className="w-4 h-3 bg-gradient-to-r from-pink-400 to-pink-500 rounded-full transform rotate-45 absolute -right-3" />
            <div className="w-3 h-3 bg-pink-500 rounded-full relative z-10" />
          </div>
        </div>

        <div className="w-full h-full rounded-full bg-gradient-to-br from-pink-200 via-pink-100 to-white border-4 border-pink-300/50 shadow-lg overflow-hidden">
          {/* Face visor */}
          <div className="absolute inset-[12%] rounded-full bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 flex items-center justify-center">
            {/* Eyes */}
            <div className={`flex ${s.gap}`}>
              <motion.div
                animate={eyeBlinkAnimation}
                className={`${s.eyes} rounded-full bg-gradient-to-b from-amber-200 to-amber-400 shadow-lg relative`}
              >
                <div className="w-[40%] h-[40%] rounded-full bg-black absolute top-[20%] left-[20%]" />
              </motion.div>
              <motion.div
                animate={{ ...eyeBlinkAnimation, transition: { ...eyeBlinkAnimation.transition, delay: 0.1 } }}
                className={`${s.eyes} rounded-full bg-gradient-to-b from-amber-200 to-amber-400 shadow-lg relative`}
              >
                <div className="w-[40%] h-[40%] rounded-full bg-black absolute top-[20%] left-[20%]" />
              </motion.div>
            </div>
          </div>
          {/* Helmet shine */}
          <div className="absolute top-[5%] left-[10%] w-[20%] h-[20%] rounded-full bg-white/40 blur-sm" />
        </div>

        {/* Headphones */}
        <div className="absolute top-[20%] -left-[10%] w-[20%] h-[30%] rounded-full bg-gradient-to-b from-pink-300 to-pink-400 border-2 border-pink-200" />
        <div className="absolute top-[20%] -right-[10%] w-[20%] h-[30%] rounded-full bg-gradient-to-b from-pink-300 to-pink-400 border-2 border-pink-200" />
      </div>

      {/* Body */}
      <div className={`${s.body} -mt-2 mx-auto bg-gradient-to-b from-pink-200 via-pink-100 to-white rounded-3xl border-2 border-pink-200/50 relative`}>
        {/* Chest badge */}
        <div className="absolute top-[15%] left-1/2 -translate-x-1/2 w-[25%] h-[25%] rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center shadow-lg">
          <span className="text-white text-[0.5em] font-bold">SM</span>
        </div>
        {/* Arm circles */}
        <div className="absolute top-[30%] -left-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-pink-200 to-pink-300 border-2 border-pink-100" />
        <div className="absolute top-[30%] -right-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-pink-200 to-pink-300 border-2 border-pink-100" />
      </div>
    </div>
  );
}

// Nova - Customer Support (Blue astronaut with headset)
export function NovaCharacter({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizes = {
    sm: { helmet: 'w-16 h-16', body: 'w-12 h-10', eyes: 'w-2.5 h-3', gap: 'gap-2' },
    md: { helmet: 'w-24 h-24', body: 'w-20 h-16', eyes: 'w-4 h-5', gap: 'gap-3' },
    lg: { helmet: 'w-40 h-40', body: 'w-32 h-28', eyes: 'w-7 h-9', gap: 'gap-6' },
  };
  const s = sizes[size];

  return (
    <div className="relative">
      <div className="absolute -inset-4 bg-gradient-to-br from-cyan-400/30 to-blue-500/30 rounded-full blur-2xl" />

      <div className={`relative ${s.helmet}`}>
        <div className="w-full h-full rounded-full bg-gradient-to-br from-cyan-200 via-blue-100 to-white border-4 border-cyan-300/50 shadow-lg overflow-hidden">
          <div className="absolute inset-[12%] rounded-full bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 flex items-center justify-center">
            <div className={`flex ${s.gap}`}>
              <motion.div animate={eyeBlinkAnimation} className={`${s.eyes} rounded-full bg-gradient-to-b from-cyan-300 to-cyan-500 shadow-lg relative`}>
                <div className="w-[40%] h-[40%] rounded-full bg-black absolute top-[20%] left-[20%]" />
              </motion.div>
              <motion.div animate={{ ...eyeBlinkAnimation, transition: { ...eyeBlinkAnimation.transition, delay: 0.1 } }} className={`${s.eyes} rounded-full bg-gradient-to-b from-cyan-300 to-cyan-500 shadow-lg relative`}>
                <div className="w-[40%] h-[40%] rounded-full bg-black absolute top-[20%] left-[20%]" />
              </motion.div>
            </div>
          </div>
          <div className="absolute top-[5%] left-[10%] w-[20%] h-[20%] rounded-full bg-white/40 blur-sm" />
        </div>

        {/* Headset with mic */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[110%] h-[15%] rounded-t-full border-4 border-cyan-400 border-b-0" />
        <div className="absolute top-[20%] -left-[12%] w-[22%] h-[28%] rounded-full bg-gradient-to-b from-cyan-400 to-cyan-500 border-2 border-cyan-300" />
        <div className="absolute top-[20%] -right-[12%] w-[22%] h-[28%] rounded-full bg-gradient-to-b from-cyan-400 to-cyan-500 border-2 border-cyan-300" />
        {/* Mic arm */}
        <div className="absolute top-[45%] -left-[5%] w-[35%] h-[4%] bg-cyan-400 rounded-full origin-right rotate-45" />
        <div className="absolute top-[55%] -left-[18%] w-[12%] h-[12%] rounded-full bg-gradient-to-b from-gray-700 to-gray-800 border-2 border-cyan-400" />
      </div>

      <div className={`${s.body} -mt-2 mx-auto bg-gradient-to-b from-cyan-200 via-blue-100 to-white rounded-3xl border-2 border-cyan-200/50 relative`}>
        <div className="absolute top-[15%] left-1/2 -translate-x-1/2 w-[25%] h-[25%] rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg">
          <span className="text-white text-[0.4em]">💬</span>
        </div>
        <div className="absolute top-[30%] -left-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-cyan-200 to-cyan-300 border-2 border-cyan-100" />
        <div className="absolute top-[30%] -right-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-cyan-200 to-cyan-300 border-2 border-cyan-100" />
      </div>
    </div>
  );
}

// Atlas - Data Analyst (Purple astronaut with glasses/visor)
export function AtlasCharacter({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizes = {
    sm: { helmet: 'w-16 h-16', body: 'w-12 h-10', eyes: 'w-3 h-2.5', gap: 'gap-1' },
    md: { helmet: 'w-24 h-24', body: 'w-20 h-16', eyes: 'w-5 h-4', gap: 'gap-1.5' },
    lg: { helmet: 'w-40 h-40', body: 'w-32 h-28', eyes: 'w-8 h-6', gap: 'gap-2' },
  };
  const s = sizes[size];

  return (
    <div className="relative">
      <div className="absolute -inset-4 bg-gradient-to-br from-violet-400/30 to-purple-500/30 rounded-full blur-2xl" />

      <div className={`relative ${s.helmet}`}>
        <div className="w-full h-full rounded-full bg-gradient-to-br from-violet-200 via-purple-100 to-white border-4 border-violet-300/50 shadow-lg overflow-hidden">
          <div className="absolute inset-[12%] rounded-full bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 flex items-center justify-center">
            {/* Square glasses/visor eyes */}
            <div className={`flex ${s.gap}`}>
              <motion.div animate={eyeBlinkAnimation} className={`${s.eyes} rounded-sm bg-gradient-to-b from-violet-300 to-violet-500 shadow-lg relative border border-violet-600`}>
                <div className="w-[35%] h-[35%] rounded-full bg-black absolute top-[30%] left-[30%]" />
                {/* Data lines */}
                <div className="absolute bottom-[10%] left-[10%] right-[10%] h-[2px] bg-violet-300/50" />
              </motion.div>
              <motion.div animate={{ ...eyeBlinkAnimation, transition: { ...eyeBlinkAnimation.transition, delay: 0.1 } }} className={`${s.eyes} rounded-sm bg-gradient-to-b from-violet-300 to-violet-500 shadow-lg relative border border-violet-600`}>
                <div className="w-[35%] h-[35%] rounded-full bg-black absolute top-[30%] left-[30%]" />
                <div className="absolute bottom-[10%] left-[10%] right-[10%] h-[2px] bg-violet-300/50" />
              </motion.div>
            </div>
            {/* Glasses bridge */}
            <div className="absolute top-[45%] left-1/2 -translate-x-1/2 w-[10%] h-[8%] bg-violet-600 rounded-full" />
          </div>
          <div className="absolute top-[5%] left-[10%] w-[20%] h-[20%] rounded-full bg-white/40 blur-sm" />
        </div>

        {/* Antenna for data */}
        <div className="absolute -top-[15%] left-1/2 -translate-x-1/2 w-[3%] h-[15%] bg-violet-400" />
        <motion.div
          animate={{ scale: [1, 1.3, 1], opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="absolute -top-[20%] left-1/2 -translate-x-1/2 w-[8%] h-[8%] rounded-full bg-violet-400"
        />
      </div>

      <div className={`${s.body} -mt-2 mx-auto bg-gradient-to-b from-violet-200 via-purple-100 to-white rounded-3xl border-2 border-violet-200/50 relative`}>
        <div className="absolute top-[15%] left-1/2 -translate-x-1/2 w-[25%] h-[25%] rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg">
          <span className="text-white text-[0.4em]">📊</span>
        </div>
        <div className="absolute top-[30%] -left-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-violet-200 to-violet-300 border-2 border-violet-100" />
        <div className="absolute top-[30%] -right-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-violet-200 to-violet-300 border-2 border-violet-100" />
      </div>
    </div>
  );
}

// Spark - Sales (Green astronaut with star badge)
export function SparkCharacter({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizes = {
    sm: { helmet: 'w-16 h-16', body: 'w-12 h-10', eyes: 'w-2.5 h-3', gap: 'gap-2' },
    md: { helmet: 'w-24 h-24', body: 'w-20 h-16', eyes: 'w-4 h-5', gap: 'gap-3' },
    lg: { helmet: 'w-40 h-40', body: 'w-32 h-28', eyes: 'w-7 h-9', gap: 'gap-6' },
  };
  const s = sizes[size];

  return (
    <div className="relative">
      <div className="absolute -inset-4 bg-gradient-to-br from-emerald-400/30 to-green-500/30 rounded-full blur-2xl" />

      <div className={`relative ${s.helmet}`}>
        <div className="w-full h-full rounded-full bg-gradient-to-br from-emerald-200 via-green-100 to-white border-4 border-emerald-300/50 shadow-lg overflow-hidden">
          <div className="absolute inset-[12%] rounded-full bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 flex items-center justify-center">
            {/* Confident/winking eyes */}
            <div className={`flex ${s.gap}`}>
              <motion.div animate={eyeBlinkAnimation} className={`${s.eyes} rounded-full bg-gradient-to-b from-emerald-300 to-emerald-500 shadow-lg relative`}>
                <div className="w-[40%] h-[40%] rounded-full bg-black absolute top-[20%] left-[20%]" />
                {/* Sparkle */}
                <div className="absolute top-0 right-0 w-[20%] h-[20%] bg-white rounded-full" />
              </motion.div>
              {/* Winking eye */}
              <div className={`${s.eyes} flex items-center justify-center`}>
                <div className="w-full h-[15%] bg-gradient-to-r from-emerald-300 to-emerald-500 rounded-full" />
              </div>
            </div>
          </div>
          <div className="absolute top-[5%] left-[10%] w-[20%] h-[20%] rounded-full bg-white/40 blur-sm" />
        </div>

        {/* Dollar signs floating */}
        <motion.div
          animate={{ y: [-5, -15, -5], opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute -top-[10%] -right-[5%] text-emerald-400 text-[0.6em] font-bold"
        >
          $
        </motion.div>
      </div>

      <div className={`${s.body} -mt-2 mx-auto bg-gradient-to-b from-emerald-200 via-green-100 to-white rounded-3xl border-2 border-emerald-200/50 relative`}>
        {/* Star badge */}
        <div className="absolute top-[10%] left-1/2 -translate-x-1/2 w-[30%] h-[30%]">
          <svg viewBox="0 0 24 24" fill="url(#starGradient)" className="w-full h-full">
            <defs>
              <linearGradient id="starGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#fbbf24" />
                <stop offset="100%" stopColor="#f59e0b" />
              </linearGradient>
            </defs>
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        </div>
        <div className="absolute top-[30%] -left-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-emerald-200 to-emerald-300 border-2 border-emerald-100" />
        <div className="absolute top-[30%] -right-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-emerald-200 to-emerald-300 border-2 border-emerald-100" />
      </div>
    </div>
  );
}

// Echo - Email Marketing (Orange astronaut with envelope)
export function EchoCharacter({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizes = {
    sm: { helmet: 'w-16 h-16', body: 'w-12 h-10', eyes: 'w-2.5 h-3', gap: 'gap-2' },
    md: { helmet: 'w-24 h-24', body: 'w-20 h-16', eyes: 'w-4 h-5', gap: 'gap-3' },
    lg: { helmet: 'w-40 h-40', body: 'w-32 h-28', eyes: 'w-7 h-9', gap: 'gap-6' },
  };
  const s = sizes[size];

  return (
    <div className="relative">
      <div className="absolute -inset-4 bg-gradient-to-br from-orange-400/30 to-amber-500/30 rounded-full blur-2xl" />

      <div className={`relative ${s.helmet}`}>
        <div className="w-full h-full rounded-full bg-gradient-to-br from-orange-200 via-amber-100 to-white border-4 border-orange-300/50 shadow-lg overflow-hidden">
          <div className="absolute inset-[12%] rounded-full bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 flex items-center justify-center">
            <div className={`flex ${s.gap}`}>
              <motion.div animate={eyeBlinkAnimation} className={`${s.eyes} rounded-full bg-gradient-to-b from-orange-300 to-orange-500 shadow-lg relative`}>
                <div className="w-[40%] h-[40%] rounded-full bg-black absolute top-[20%] left-[20%]" />
              </motion.div>
              <motion.div animate={{ ...eyeBlinkAnimation, transition: { ...eyeBlinkAnimation.transition, delay: 0.1 } }} className={`${s.eyes} rounded-full bg-gradient-to-b from-orange-300 to-orange-500 shadow-lg relative`}>
                <div className="w-[40%] h-[40%] rounded-full bg-black absolute top-[20%] left-[20%]" />
              </motion.div>
            </div>
          </div>
          <div className="absolute top-[5%] left-[10%] w-[20%] h-[20%] rounded-full bg-white/40 blur-sm" />
        </div>

        {/* Floating envelope */}
        <motion.div
          animate={{ y: [-3, -10, -3], rotate: [-5, 5, -5] }}
          transition={{ duration: 2.5, repeat: Infinity }}
          className="absolute -top-[5%] -right-[15%] text-[1.2em]"
        >
          ✉️
        </motion.div>
      </div>

      <div className={`${s.body} -mt-2 mx-auto bg-gradient-to-b from-orange-200 via-amber-100 to-white rounded-3xl border-2 border-orange-200/50 relative`}>
        <div className="absolute top-[15%] left-1/2 -translate-x-1/2 w-[25%] h-[25%] rounded-full bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-lg">
          <span className="text-white text-[0.4em]">@</span>
        </div>
        <div className="absolute top-[30%] -left-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-orange-200 to-orange-300 border-2 border-orange-100" />
        <div className="absolute top-[30%] -right-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-orange-200 to-orange-300 border-2 border-orange-100" />
      </div>
    </div>
  );
}

// Code - Developer (Gray/slate astronaut with code symbols)
export function CodeCharacter({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizes = {
    sm: { helmet: 'w-16 h-16', body: 'w-12 h-10', eyes: 'w-2.5 h-3', gap: 'gap-2' },
    md: { helmet: 'w-24 h-24', body: 'w-20 h-16', eyes: 'w-4 h-5', gap: 'gap-3' },
    lg: { helmet: 'w-40 h-40', body: 'w-32 h-28', eyes: 'w-7 h-9', gap: 'gap-6' },
  };
  const s = sizes[size];

  return (
    <div className="relative">
      <div className="absolute -inset-4 bg-gradient-to-br from-slate-400/30 to-gray-500/30 rounded-full blur-2xl" />

      <div className={`relative ${s.helmet}`}>
        <div className="w-full h-full rounded-full bg-gradient-to-br from-slate-300 via-gray-200 to-white border-4 border-slate-400/50 shadow-lg overflow-hidden">
          <div className="absolute inset-[12%] rounded-full bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
            {/* Matrix-style green eyes */}
            <div className={`flex ${s.gap}`}>
              <motion.div
                animate={{ opacity: [1, 0.7, 1] }}
                transition={{ duration: 0.5, repeat: Infinity }}
                className={`${s.eyes} rounded-sm bg-gradient-to-b from-green-400 to-green-600 shadow-lg relative font-mono flex items-center justify-center`}
              >
                <span className="text-black text-[0.4em] font-bold">0</span>
              </motion.div>
              <motion.div
                animate={{ opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 0.5, repeat: Infinity }}
                className={`${s.eyes} rounded-sm bg-gradient-to-b from-green-400 to-green-600 shadow-lg relative font-mono flex items-center justify-center`}
              >
                <span className="text-black text-[0.4em] font-bold">1</span>
              </motion.div>
            </div>
          </div>
          <div className="absolute top-[5%] left-[10%] w-[20%] h-[20%] rounded-full bg-white/40 blur-sm" />
        </div>

        {/* Code brackets floating */}
        <motion.div
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1, repeat: Infinity }}
          className="absolute top-[30%] -left-[20%] text-green-400 text-[0.8em] font-mono font-bold"
        >
          {'</>'}
        </motion.div>
      </div>

      <div className={`${s.body} -mt-2 mx-auto bg-gradient-to-b from-slate-300 via-gray-200 to-white rounded-3xl border-2 border-slate-300/50 relative`}>
        <div className="absolute top-[15%] left-1/2 -translate-x-1/2 w-[25%] h-[25%] rounded-full bg-gradient-to-br from-slate-600 to-gray-800 flex items-center justify-center shadow-lg">
          <span className="text-green-400 text-[0.35em] font-mono">{'{}'}</span>
        </div>
        <div className="absolute top-[30%] -left-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-slate-300 to-slate-400 border-2 border-slate-200" />
        <div className="absolute top-[30%] -right-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-slate-300 to-slate-400 border-2 border-slate-200" />
      </div>
    </div>
  );
}

// Ink - Copywriter (Indigo astronaut with pen)
export function InkCharacter({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizes = {
    sm: { helmet: 'w-16 h-16', body: 'w-12 h-10', eyes: 'w-2.5 h-3', gap: 'gap-2' },
    md: { helmet: 'w-24 h-24', body: 'w-20 h-16', eyes: 'w-4 h-5', gap: 'gap-3' },
    lg: { helmet: 'w-40 h-40', body: 'w-32 h-28', eyes: 'w-7 h-9', gap: 'gap-6' },
  };
  const s = sizes[size];

  return (
    <div className="relative">
      <div className="absolute -inset-4 bg-gradient-to-br from-indigo-400/30 to-violet-500/30 rounded-full blur-2xl" />

      <div className={`relative ${s.helmet}`}>
        <div className="w-full h-full rounded-full bg-gradient-to-br from-indigo-200 via-violet-100 to-white border-4 border-indigo-300/50 shadow-lg overflow-hidden">
          <div className="absolute inset-[12%] rounded-full bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 flex items-center justify-center">
            {/* Thoughtful eyes - one raised eyebrow */}
            <div className={`flex ${s.gap}`}>
              <div className="relative">
                <div className="absolute -top-[30%] w-full h-[15%] bg-indigo-400 rounded-full transform -rotate-12" />
                <motion.div animate={eyeBlinkAnimation} className={`${s.eyes} rounded-full bg-gradient-to-b from-indigo-300 to-indigo-500 shadow-lg relative`}>
                  <div className="w-[40%] h-[40%] rounded-full bg-black absolute top-[20%] left-[20%]" />
                </motion.div>
              </div>
              <motion.div animate={{ ...eyeBlinkAnimation, transition: { ...eyeBlinkAnimation.transition, delay: 0.1 } }} className={`${s.eyes} rounded-full bg-gradient-to-b from-indigo-300 to-indigo-500 shadow-lg relative`}>
                <div className="w-[40%] h-[40%] rounded-full bg-black absolute top-[20%] left-[20%]" />
              </motion.div>
            </div>
          </div>
          <div className="absolute top-[5%] left-[10%] w-[20%] h-[20%] rounded-full bg-white/40 blur-sm" />
        </div>

        {/* Floating pen */}
        <motion.div
          animate={{ rotate: [-10, 10, -10], y: [-2, 2, -2] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute -top-[5%] -right-[10%] text-[1em]"
        >
          ✍️
        </motion.div>
      </div>

      <div className={`${s.body} -mt-2 mx-auto bg-gradient-to-b from-indigo-200 via-violet-100 to-white rounded-3xl border-2 border-indigo-200/50 relative`}>
        <div className="absolute top-[15%] left-1/2 -translate-x-1/2 w-[25%] h-[25%] rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg">
          <span className="text-white text-[0.35em]">Aa</span>
        </div>
        <div className="absolute top-[30%] -left-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-indigo-200 to-indigo-300 border-2 border-indigo-100" />
        <div className="absolute top-[30%] -right-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-indigo-200 to-indigo-300 border-2 border-indigo-100" />
      </div>
    </div>
  );
}

// Orbit - SEO Expert (Blue astronaut with magnifying glass)
export function OrbitCharacter({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizes = {
    sm: { helmet: 'w-16 h-16', body: 'w-12 h-10', eyes: 'w-2.5 h-3', gap: 'gap-2' },
    md: { helmet: 'w-24 h-24', body: 'w-20 h-16', eyes: 'w-4 h-5', gap: 'gap-3' },
    lg: { helmet: 'w-40 h-40', body: 'w-32 h-28', eyes: 'w-7 h-9', gap: 'gap-6' },
  };
  const s = sizes[size];

  return (
    <div className="relative">
      <div className="absolute -inset-4 bg-gradient-to-br from-blue-400/30 to-indigo-500/30 rounded-full blur-2xl" />

      <div className={`relative ${s.helmet}`}>
        {/* Orbiting ring */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          className="absolute inset-[-15%] border-2 border-blue-400/50 rounded-full border-dashed"
        />
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          className="absolute inset-[-15%]"
        >
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[10%] h-[10%] rounded-full bg-blue-400" />
        </motion.div>

        <div className="w-full h-full rounded-full bg-gradient-to-br from-blue-200 via-sky-100 to-white border-4 border-blue-300/50 shadow-lg overflow-hidden">
          <div className="absolute inset-[12%] rounded-full bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 flex items-center justify-center">
            <div className={`flex ${s.gap}`}>
              <motion.div animate={eyeBlinkAnimation} className={`${s.eyes} rounded-full bg-gradient-to-b from-blue-300 to-blue-500 shadow-lg relative`}>
                <div className="w-[40%] h-[40%] rounded-full bg-black absolute top-[20%] left-[20%]" />
              </motion.div>
              <motion.div animate={{ ...eyeBlinkAnimation, transition: { ...eyeBlinkAnimation.transition, delay: 0.1 } }} className={`${s.eyes} rounded-full bg-gradient-to-b from-blue-300 to-blue-500 shadow-lg relative`}>
                <div className="w-[40%] h-[40%] rounded-full bg-black absolute top-[20%] left-[20%]" />
              </motion.div>
            </div>
          </div>
          <div className="absolute top-[5%] left-[10%] w-[20%] h-[20%] rounded-full bg-white/40 blur-sm" />
        </div>
      </div>

      <div className={`${s.body} -mt-2 mx-auto bg-gradient-to-b from-blue-200 via-sky-100 to-white rounded-3xl border-2 border-blue-200/50 relative`}>
        <div className="absolute top-[15%] left-1/2 -translate-x-1/2 w-[25%] h-[25%] rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
          <span className="text-white text-[0.4em]">🔍</span>
        </div>
        <div className="absolute top-[30%] -left-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-blue-200 to-blue-300 border-2 border-blue-100" />
        <div className="absolute top-[30%] -right-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-blue-200 to-blue-300 border-2 border-blue-100" />
      </div>
    </div>
  );
}

// Sage - Business Strategist (Emerald astronaut with lightbulb)
export function SageCharacter({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizes = {
    sm: { helmet: 'w-16 h-16', body: 'w-12 h-10', eyes: 'w-2.5 h-3', gap: 'gap-2' },
    md: { helmet: 'w-24 h-24', body: 'w-20 h-16', eyes: 'w-4 h-5', gap: 'gap-3' },
    lg: { helmet: 'w-40 h-40', body: 'w-32 h-28', eyes: 'w-7 h-9', gap: 'gap-6' },
  };
  const s = sizes[size];

  return (
    <div className="relative">
      <div className="absolute -inset-4 bg-gradient-to-br from-teal-400/30 to-emerald-500/30 rounded-full blur-2xl" />

      <div className={`relative ${s.helmet}`}>
        {/* Lightbulb on top */}
        <motion.div
          animate={{ opacity: [0.5, 1, 0.5], scale: [0.95, 1.05, 0.95] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute -top-[25%] left-1/2 -translate-x-1/2 text-[1.2em]"
        >
          💡
        </motion.div>

        <div className="w-full h-full rounded-full bg-gradient-to-br from-teal-200 via-emerald-100 to-white border-4 border-teal-300/50 shadow-lg overflow-hidden">
          <div className="absolute inset-[12%] rounded-full bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 flex items-center justify-center">
            {/* Wise, calm eyes */}
            <div className={`flex ${s.gap}`}>
              <motion.div animate={eyeBlinkAnimation} className={`${s.eyes} rounded-full bg-gradient-to-b from-teal-300 to-teal-500 shadow-lg relative`}>
                <div className="w-[40%] h-[40%] rounded-full bg-black absolute top-[25%] left-[30%]" />
              </motion.div>
              <motion.div animate={{ ...eyeBlinkAnimation, transition: { ...eyeBlinkAnimation.transition, delay: 0.1 } }} className={`${s.eyes} rounded-full bg-gradient-to-b from-teal-300 to-teal-500 shadow-lg relative`}>
                <div className="w-[40%] h-[40%] rounded-full bg-black absolute top-[25%] left-[30%]" />
              </motion.div>
            </div>
          </div>
          <div className="absolute top-[5%] left-[10%] w-[20%] h-[20%] rounded-full bg-white/40 blur-sm" />
        </div>
      </div>

      <div className={`${s.body} -mt-2 mx-auto bg-gradient-to-b from-teal-200 via-emerald-100 to-white rounded-3xl border-2 border-teal-200/50 relative`}>
        <div className="absolute top-[15%] left-1/2 -translate-x-1/2 w-[25%] h-[25%] rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-lg">
          <span className="text-white text-[0.4em]">🧠</span>
        </div>
        <div className="absolute top-[30%] -left-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-teal-200 to-teal-300 border-2 border-teal-100" />
        <div className="absolute top-[30%] -right-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-teal-200 to-teal-300 border-2 border-teal-100" />
      </div>
    </div>
  );
}

// Scout - Recruiter (Teal astronaut with binoculars)
export function ScoutCharacter({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizes = {
    sm: { helmet: 'w-16 h-16', body: 'w-12 h-10', eyes: 'w-3 h-3', gap: 'gap-0.5' },
    md: { helmet: 'w-24 h-24', body: 'w-20 h-16', eyes: 'w-5 h-5', gap: 'gap-1' },
    lg: { helmet: 'w-40 h-40', body: 'w-32 h-28', eyes: 'w-8 h-8', gap: 'gap-1.5' },
  };
  const s = sizes[size];

  return (
    <div className="relative">
      <div className="absolute -inset-4 bg-gradient-to-br from-cyan-400/30 to-teal-500/30 rounded-full blur-2xl" />

      <div className={`relative ${s.helmet}`}>
        <div className="w-full h-full rounded-full bg-gradient-to-br from-cyan-200 via-teal-100 to-white border-4 border-cyan-300/50 shadow-lg overflow-hidden">
          <div className="absolute inset-[12%] rounded-full bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 flex items-center justify-center">
            {/* Binocular-like eyes */}
            <div className={`flex ${s.gap}`}>
              <motion.div
                animate={{ scale: [1, 1.15, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className={`${s.eyes} rounded-full border-4 border-cyan-400 bg-gradient-to-b from-cyan-300 to-cyan-500 shadow-lg relative`}
              >
                <div className="w-[35%] h-[35%] rounded-full bg-black absolute top-[30%] left-[30%]" />
                {/* Lens glare */}
                <div className="absolute top-[15%] right-[15%] w-[20%] h-[20%] rounded-full bg-white/60" />
              </motion.div>
              <motion.div
                animate={{ scale: [1, 1.15, 1] }}
                transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}
                className={`${s.eyes} rounded-full border-4 border-cyan-400 bg-gradient-to-b from-cyan-300 to-cyan-500 shadow-lg relative`}
              >
                <div className="w-[35%] h-[35%] rounded-full bg-black absolute top-[30%] left-[30%]" />
                <div className="absolute top-[15%] right-[15%] w-[20%] h-[20%] rounded-full bg-white/60" />
              </motion.div>
            </div>
            {/* Binocular bridge */}
            <div className="absolute top-[42%] left-1/2 -translate-x-1/2 w-[15%] h-[12%] bg-cyan-500 rounded-sm" />
          </div>
          <div className="absolute top-[5%] left-[10%] w-[20%] h-[20%] rounded-full bg-white/40 blur-sm" />
        </div>
      </div>

      <div className={`${s.body} -mt-2 mx-auto bg-gradient-to-b from-cyan-200 via-teal-100 to-white rounded-3xl border-2 border-cyan-200/50 relative`}>
        <div className="absolute top-[15%] left-1/2 -translate-x-1/2 w-[25%] h-[25%] rounded-full bg-gradient-to-br from-cyan-500 to-teal-600 flex items-center justify-center shadow-lg">
          <span className="text-white text-[0.4em]">👤</span>
        </div>
        <div className="absolute top-[30%] -left-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-cyan-200 to-cyan-300 border-2 border-cyan-100" />
        <div className="absolute top-[30%] -right-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-cyan-200 to-cyan-300 border-2 border-cyan-100" />
      </div>
    </div>
  );
}

// Pulse - Personal Growth (Red astronaut with muscles)
export function PulseCharacter({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizes = {
    sm: { helmet: 'w-16 h-16', body: 'w-12 h-10', eyes: 'w-2.5 h-3', gap: 'gap-2' },
    md: { helmet: 'w-24 h-24', body: 'w-20 h-16', eyes: 'w-4 h-5', gap: 'gap-3' },
    lg: { helmet: 'w-40 h-40', body: 'w-32 h-28', eyes: 'w-7 h-9', gap: 'gap-6' },
  };
  const s = sizes[size];

  return (
    <div className="relative">
      <div className="absolute -inset-4 bg-gradient-to-br from-red-400/30 to-rose-500/30 rounded-full blur-2xl" />

      <div className={`relative ${s.helmet}`}>
        {/* Floating dumbbell */}
        <motion.div
          animate={{ rotate: [-10, 10, -10], y: [-2, 2, -2] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="absolute -top-[10%] -right-[10%] text-[0.8em]"
        >
          💪
        </motion.div>

        <div className="w-full h-full rounded-full bg-gradient-to-br from-red-200 via-rose-100 to-white border-4 border-red-300/50 shadow-lg overflow-hidden">
          <div className="absolute inset-[12%] rounded-full bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 flex items-center justify-center">
            {/* Determined eyes */}
            <div className={`flex ${s.gap}`}>
              <motion.div animate={eyeBlinkAnimation} className={`${s.eyes} rounded-full bg-gradient-to-b from-red-300 to-red-500 shadow-lg relative`}>
                <div className="w-[40%] h-[40%] rounded-full bg-black absolute top-[20%] left-[20%]" />
              </motion.div>
              <motion.div animate={{ ...eyeBlinkAnimation, transition: { ...eyeBlinkAnimation.transition, delay: 0.1 } }} className={`${s.eyes} rounded-full bg-gradient-to-b from-red-300 to-red-500 shadow-lg relative`}>
                <div className="w-[40%] h-[40%] rounded-full bg-black absolute top-[20%] left-[20%]" />
              </motion.div>
            </div>
          </div>
          <div className="absolute top-[5%] left-[10%] w-[20%] h-[20%] rounded-full bg-white/40 blur-sm" />
        </div>
      </div>

      <div className={`${s.body} -mt-2 mx-auto bg-gradient-to-b from-red-200 via-rose-100 to-white rounded-3xl border-2 border-red-200/50 relative`}>
        <div className="absolute top-[15%] left-1/2 -translate-x-1/2 w-[25%] h-[25%] rounded-full bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center shadow-lg">
          <span className="text-white text-[0.4em]">❤️</span>
        </div>
        <div className="absolute top-[30%] -left-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-red-200 to-red-300 border-2 border-red-100" />
        <div className="absolute top-[30%] -right-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-red-200 to-red-300 border-2 border-red-100" />
      </div>
    </div>
  );
}

// Flux - eCommerce Manager (Amber/yellow astronaut with shopping cart)
export function FluxCharacter({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizes = {
    sm: { helmet: 'w-16 h-16', body: 'w-12 h-10', eyes: 'w-2.5 h-3', gap: 'gap-2' },
    md: { helmet: 'w-24 h-24', body: 'w-20 h-16', eyes: 'w-4 h-5', gap: 'gap-3' },
    lg: { helmet: 'w-40 h-40', body: 'w-32 h-28', eyes: 'w-7 h-9', gap: 'gap-6' },
  };
  const s = sizes[size];

  return (
    <div className="relative">
      <div className="absolute -inset-4 bg-gradient-to-br from-amber-400/30 to-yellow-500/30 rounded-full blur-2xl" />

      <div className={`relative ${s.helmet}`}>
        {/* Shopping cart */}
        <motion.div
          animate={{ x: [-2, 2, -2] }}
          transition={{ duration: 1, repeat: Infinity }}
          className="absolute -top-[5%] -right-[15%] text-[0.9em]"
        >
          🛒
        </motion.div>

        <div className="w-full h-full rounded-full bg-gradient-to-br from-amber-200 via-yellow-100 to-white border-4 border-amber-300/50 shadow-lg overflow-hidden">
          <div className="absolute inset-[12%] rounded-full bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 flex items-center justify-center">
            <div className={`flex ${s.gap}`}>
              <motion.div animate={eyeBlinkAnimation} className={`${s.eyes} rounded-full bg-gradient-to-b from-amber-300 to-amber-500 shadow-lg relative`}>
                <div className="w-[40%] h-[40%] rounded-full bg-black absolute top-[20%] left-[20%]" />
              </motion.div>
              <motion.div animate={{ ...eyeBlinkAnimation, transition: { ...eyeBlinkAnimation.transition, delay: 0.1 } }} className={`${s.eyes} rounded-full bg-gradient-to-b from-amber-300 to-amber-500 shadow-lg relative`}>
                <div className="w-[40%] h-[40%] rounded-full bg-black absolute top-[20%] left-[20%]" />
              </motion.div>
            </div>
          </div>
          <div className="absolute top-[5%] left-[10%] w-[20%] h-[20%] rounded-full bg-white/40 blur-sm" />
        </div>
      </div>

      <div className={`${s.body} -mt-2 mx-auto bg-gradient-to-b from-amber-200 via-yellow-100 to-white rounded-3xl border-2 border-amber-200/50 relative`}>
        <div className="absolute top-[15%] left-1/2 -translate-x-1/2 w-[25%] h-[25%] rounded-full bg-gradient-to-br from-amber-500 to-yellow-600 flex items-center justify-center shadow-lg">
          <span className="text-white text-[0.4em]">💰</span>
        </div>
        <div className="absolute top-[30%] -left-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-amber-200 to-amber-300 border-2 border-amber-100" />
        <div className="absolute top-[30%] -right-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-amber-200 to-amber-300 border-2 border-amber-100" />
      </div>
    </div>
  );
}

// Sync - Virtual Assistant (Purple/fuchsia astronaut with calendar)
export function SyncCharacter({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizes = {
    sm: { helmet: 'w-16 h-16', body: 'w-12 h-10', eyes: 'w-2.5 h-3', gap: 'gap-2' },
    md: { helmet: 'w-24 h-24', body: 'w-20 h-16', eyes: 'w-4 h-5', gap: 'gap-3' },
    lg: { helmet: 'w-40 h-40', body: 'w-32 h-28', eyes: 'w-7 h-9', gap: 'gap-6' },
  };
  const s = sizes[size];

  return (
    <div className="relative">
      <div className="absolute -inset-4 bg-gradient-to-br from-purple-400/30 to-fuchsia-500/30 rounded-full blur-2xl" />

      <div className={`relative ${s.helmet}`}>
        {/* Sync arrows */}
        <motion.div
          animate={{ rotate: [0, 360] }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          className="absolute -top-[10%] -right-[5%] text-[0.7em]"
        >
          🔄
        </motion.div>

        <div className="w-full h-full rounded-full bg-gradient-to-br from-purple-200 via-fuchsia-100 to-white border-4 border-purple-300/50 shadow-lg overflow-hidden">
          <div className="absolute inset-[12%] rounded-full bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 flex items-center justify-center">
            <div className={`flex ${s.gap}`}>
              <motion.div animate={eyeBlinkAnimation} className={`${s.eyes} rounded-full bg-gradient-to-b from-purple-300 to-purple-500 shadow-lg relative`}>
                <div className="w-[40%] h-[40%] rounded-full bg-black absolute top-[20%] left-[20%]" />
              </motion.div>
              <motion.div animate={{ ...eyeBlinkAnimation, transition: { ...eyeBlinkAnimation.transition, delay: 0.1 } }} className={`${s.eyes} rounded-full bg-gradient-to-b from-purple-300 to-purple-500 shadow-lg relative`}>
                <div className="w-[40%] h-[40%] rounded-full bg-black absolute top-[20%] left-[20%]" />
              </motion.div>
            </div>
          </div>
          <div className="absolute top-[5%] left-[10%] w-[20%] h-[20%] rounded-full bg-white/40 blur-sm" />
        </div>
      </div>

      <div className={`${s.body} -mt-2 mx-auto bg-gradient-to-b from-purple-200 via-fuchsia-100 to-white rounded-3xl border-2 border-purple-200/50 relative`}>
        <div className="absolute top-[15%] left-1/2 -translate-x-1/2 w-[25%] h-[25%] rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-600 flex items-center justify-center shadow-lg">
          <span className="text-white text-[0.4em]">📅</span>
        </div>
        <div className="absolute top-[30%] -left-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-purple-200 to-purple-300 border-2 border-purple-100" />
        <div className="absolute top-[30%] -right-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-purple-200 to-purple-300 border-2 border-purple-100" />
      </div>
    </div>
  );
}

// Pixel - Design Assistant (Pink astronaut with paint palette)
export function PixelCharacter({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizes = {
    sm: { helmet: 'w-16 h-16', body: 'w-12 h-10', eyes: 'w-2.5 h-3', gap: 'gap-2' },
    md: { helmet: 'w-24 h-24', body: 'w-20 h-16', eyes: 'w-4 h-5', gap: 'gap-3' },
    lg: { helmet: 'w-40 h-40', body: 'w-32 h-28', eyes: 'w-7 h-9', gap: 'gap-6' },
  };
  const s = sizes[size];

  return (
    <div className="relative">
      <div className="absolute -inset-4 bg-gradient-to-br from-pink-400/30 to-rose-400/30 rounded-full blur-2xl" />

      <div className={`relative ${s.helmet}`}>
        {/* Paintbrush */}
        <motion.div
          animate={{ rotate: [-15, 15, -15] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="absolute -top-[5%] -right-[12%] text-[0.9em]"
        >
          🎨
        </motion.div>

        <div className="w-full h-full rounded-full bg-gradient-to-br from-pink-200 via-rose-100 to-white border-4 border-pink-300/50 shadow-lg overflow-hidden">
          <div className="absolute inset-[12%] rounded-full bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 flex items-center justify-center">
            {/* Creative sparkle eyes */}
            <div className={`flex ${s.gap}`}>
              <motion.div animate={eyeBlinkAnimation} className={`${s.eyes} rounded-full bg-gradient-to-b from-pink-300 to-pink-500 shadow-lg relative`}>
                <div className="w-[40%] h-[40%] rounded-full bg-black absolute top-[20%] left-[20%]" />
                <div className="absolute top-[10%] right-[10%] w-[15%] h-[15%] bg-white rounded-full" />
              </motion.div>
              <motion.div animate={{ ...eyeBlinkAnimation, transition: { ...eyeBlinkAnimation.transition, delay: 0.1 } }} className={`${s.eyes} rounded-full bg-gradient-to-b from-pink-300 to-pink-500 shadow-lg relative`}>
                <div className="w-[40%] h-[40%] rounded-full bg-black absolute top-[20%] left-[20%]" />
                <div className="absolute top-[10%] right-[10%] w-[15%] h-[15%] bg-white rounded-full" />
              </motion.div>
            </div>
          </div>
          <div className="absolute top-[5%] left-[10%] w-[20%] h-[20%] rounded-full bg-white/40 blur-sm" />
        </div>
      </div>

      <div className={`${s.body} -mt-2 mx-auto bg-gradient-to-b from-pink-200 via-rose-100 to-white rounded-3xl border-2 border-pink-200/50 relative`}>
        <div className="absolute top-[15%] left-1/2 -translate-x-1/2 w-[25%] h-[25%] rounded-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center shadow-lg">
          <span className="text-white text-[0.4em]">✨</span>
        </div>
        <div className="absolute top-[30%] -left-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-pink-200 to-pink-300 border-2 border-pink-100" />
        <div className="absolute top-[30%] -right-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-pink-200 to-pink-300 border-2 border-pink-100" />
      </div>
    </div>
  );
}

// Legal - Compliance Assistant (Stone/neutral astronaut with scales)
export function LegalCharacter({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizes = {
    sm: { helmet: 'w-16 h-16', body: 'w-12 h-10', eyes: 'w-2.5 h-3', gap: 'gap-2' },
    md: { helmet: 'w-24 h-24', body: 'w-20 h-16', eyes: 'w-4 h-5', gap: 'gap-3' },
    lg: { helmet: 'w-40 h-40', body: 'w-32 h-28', eyes: 'w-7 h-9', gap: 'gap-6' },
  };
  const s = sizes[size];

  return (
    <div className="relative">
      <div className="absolute -inset-4 bg-gradient-to-br from-stone-400/30 to-neutral-500/30 rounded-full blur-2xl" />

      <div className={`relative ${s.helmet}`}>
        {/* Scales of justice */}
        <motion.div
          animate={{ rotate: [-5, 5, -5] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute -top-[15%] left-1/2 -translate-x-1/2 text-[0.9em]"
        >
          ⚖️
        </motion.div>

        <div className="w-full h-full rounded-full bg-gradient-to-br from-stone-200 via-neutral-100 to-white border-4 border-stone-300/50 shadow-lg overflow-hidden">
          <div className="absolute inset-[12%] rounded-full bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 flex items-center justify-center">
            {/* Serious professional eyes */}
            <div className={`flex ${s.gap}`}>
              <motion.div animate={eyeBlinkAnimation} className={`${s.eyes} rounded-sm bg-gradient-to-b from-stone-300 to-stone-500 shadow-lg relative`}>
                <div className="w-[40%] h-[40%] rounded-full bg-black absolute top-[25%] left-[25%]" />
              </motion.div>
              <motion.div animate={{ ...eyeBlinkAnimation, transition: { ...eyeBlinkAnimation.transition, delay: 0.1 } }} className={`${s.eyes} rounded-sm bg-gradient-to-b from-stone-300 to-stone-500 shadow-lg relative`}>
                <div className="w-[40%] h-[40%] rounded-full bg-black absolute top-[25%] left-[25%]" />
              </motion.div>
            </div>
          </div>
          <div className="absolute top-[5%] left-[10%] w-[20%] h-[20%] rounded-full bg-white/40 blur-sm" />
        </div>
      </div>

      <div className={`${s.body} -mt-2 mx-auto bg-gradient-to-b from-stone-200 via-neutral-100 to-white rounded-3xl border-2 border-stone-200/50 relative`}>
        <div className="absolute top-[15%] left-1/2 -translate-x-1/2 w-[25%] h-[25%] rounded-full bg-gradient-to-br from-stone-500 to-neutral-600 flex items-center justify-center shadow-lg">
          <span className="text-white text-[0.4em]">📜</span>
        </div>
        <div className="absolute top-[30%] -left-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-stone-200 to-stone-300 border-2 border-stone-100" />
        <div className="absolute top-[30%] -right-[15%] w-[25%] h-[25%] rounded-full bg-gradient-to-br from-stone-200 to-stone-300 border-2 border-stone-100" />
      </div>
    </div>
  );
}

// Export all characters
export const AICharacters = {
  Aria: AriaCharacter,
  Nova: NovaCharacter,
  Atlas: AtlasCharacter,
  Spark: SparkCharacter,
  Echo: EchoCharacter,
  Code: CodeCharacter,
  Ink: InkCharacter,
  Orbit: OrbitCharacter,
  Sage: SageCharacter,
  Scout: ScoutCharacter,
  Pulse: PulseCharacter,
  Flux: FluxCharacter,
  Sync: SyncCharacter,
  Pixel: PixelCharacter,
  Legal: LegalCharacter,
};

export default AICharacters;
